// WEL-5236 gift order contract (pure)

import type { ServerResponse } from '@core/utils';

import { ANNUAL_DONATION_LIMIT } from '@/constants/hometownDonationPolicy';
import {
  YEAR_END_DELIVERY_CONSENT_REQUIRED,
  YEAR_END_GIFT_OPTION,
  YEAR_END_GIFT_SECTION,
  type YearEndGiftOption,
  type YearEndGiftSection,
} from '@/constants/yearEndGoodsGift';

import { CUSTOM_DELIVERY_OPTION } from '../_components/delivery/constants';

export const MAX_GIFT_ORDER_QUANTITY = 99;

export type SubmitDonationParams = {
  donationAmount: number;
  homeDonationBusinessId?: number;
  homeInstitutionId: number;
  registrationNo: string;
  region?: string;
  productNumber?: string;
  optionItemIds?: number[];
  addressId?: number;
  deliveryMessage?: string;
  isOfflineProduct?: boolean;
  quantity?: number;
  editionGiftChoice?: YearEndGiftOption;
};

export const buildProcessDonationBody = (
  { quantity = 1, ...params }: SubmitDonationParams,
  pinkey?: string,
  utm?: Record<string, string>,
) => {
  if (params.productNumber && params.isOfflineProduct && !params.addressId) {
    throw new Error('오프라인 상품은 배송지를 선택해야 합니다.');
  }

  return {
    registrationNo: params.registrationNo,
    homeInstitutionId: params.homeInstitutionId,
    donationAmount: params.donationAmount,
    homeDonationBusinessId: params.homeDonationBusinessId,
    pinkey: pinkey || undefined,
    utm,
    region: params.region,
    productNumber: params.productNumber,
    optionItemIds: params.optionItemIds,
    quantity: params.productNumber ? quantity : undefined,
    addressId: params.addressId,
    deliveryMessage: params.deliveryMessage,
    editionGiftChoice: params.editionGiftChoice,
  };
};

//* 서버는 대상 기부가 아닌데 선물 필드가 오면 거절한다 — 고를 수 있는 칸일 때만 싣는다
//* 다이어리 배송지는 주문 종류와 관계없이 addressId · deliveryMessage 다. 동의는 요청 자체로 본다
export const resolveEditionGiftParams = ({
  section,
  option,
  addressId,
  deliveryMessage,
}: {
  section: YearEndGiftSection;
  option: YearEndGiftOption | null;
  addressId?: number;
  deliveryMessage?: string;
}): Pick<
  SubmitDonationParams,
  'editionGiftChoice' | 'addressId' | 'deliveryMessage'
> => {
  if (section !== YEAR_END_GIFT_SECTION.SELECTABLE || !option) {
    return {};
  }

  if (option === YEAR_END_GIFT_OPTION.POINT) {
    return { editionGiftChoice: option };
  }

  return { editionGiftChoice: option, addressId, deliveryMessage };
};

export const EDITION_PROCESS_ERROR = {
  GIFT_CHOICE_MISSING: 'GIFT_CHOICE_MISSING',
  DELIVERY_ADDRESS_MISSING: 'DELIVERY_ADDRESS_MISSING',
  SOLD_OUT: 'SOLD_OUT',
} as const;

export type EditionProcessError =
  (typeof EDITION_PROCESS_ERROR)[keyof typeof EDITION_PROCESS_ERROR];

const EDITION_REQUIRED_CODE = 30400;
const EDITION_SOLD_OUT_CODE = 30422;

//* 30400 세 종류는 코드가 같아 서버 문구로만 갈린다
export const resolveEditionProcessError = (
  error: unknown,
): EditionProcessError | null => {
  const { return_code: returnCode, return_message: returnMessage = '' } =
    error as Partial<ServerResponse>;

  if (returnCode === EDITION_SOLD_OUT_CODE) {
    return EDITION_PROCESS_ERROR.SOLD_OUT;
  }

  if (returnCode !== EDITION_REQUIRED_CODE) {
    return null;
  }

  if (returnMessage.includes('배송지')) {
    return EDITION_PROCESS_ERROR.DELIVERY_ADDRESS_MISSING;
  }

  return EDITION_PROCESS_ERROR.GIFT_CHOICE_MISSING;
};

export type DonationProductInfo = {
  productNumber: string;
  productName: string;
  imageUrl?: string;
  institutionName?: string;
  institutionLogoUrl?: string;
  optionItemIds?: number[];
  optionName?: string;
  minDonationAmount: number;
  isOfflineProduct: boolean;
  deliveryExclusionRegionNames: string[];
};

export type GiftProductSource = {
  productName: string;
  mainImageUrl?: string | null;
  institutionName?: string | null;
  institutionLogoUrl?: string | null;
  currentPrice: number;
  onlineProductYN: boolean;
  deliveryExclusionRegionList?: { regionName: string }[] | null;
  optionGroupInfoList?:
    | {
        optionItemInfoList?:
          | {
              optionItemId: number;
              itemName?: string | null;
              extraProductPrice?: number | null;
            }[]
          | null;
      }[]
    | null;
};

//* 서버 ProductUtil.getDonationAmount 와 같은 산식 — 상품가·옵션가를 먼저 더하고 한 번만 천원 올림해야 서버 검증값과 일치한다.
export const calculateRequiredDonationAmount = (productPrice: number): number =>
  Math.ceil((productPrice * 10) / 3 / 1000) * 1000;

export const parseOptionItemIds = (
  raw: string | null | undefined,
): number[] | undefined => {
  if (!raw) {
    return undefined;
  }

  const optionItemIds = raw
    .split(',')
    .map((token) => token.trim())
    .filter((token) => token.length > 0)
    .map(Number)
    .filter(
      (optionItemId) => Number.isInteger(optionItemId) && optionItemId > 0,
    );

  return optionItemIds.length > 0 ? optionItemIds : undefined;
};

export const parseOrderQuantity = (
  raw: string | null | undefined,
): number | undefined => {
  if (!raw) {
    return undefined;
  }

  const quantity = Number(raw);
  if (!Number.isInteger(quantity)) {
    return undefined;
  }

  return Math.min(Math.max(quantity, 1), MAX_GIFT_ORDER_QUANTITY);
};

export const serializeOptionItemIds = (
  optionItemIds: number[],
): string | undefined => {
  const uniqueOptionItemIds = [...new Set(optionItemIds)];

  return uniqueOptionItemIds.length > 0
    ? uniqueOptionItemIds.join(',')
    : undefined;
};

/** 주문서(`/donation/order`)는 `donationBusinessId` 또는 `institutionId`+`institutionName` 중 하나를 반드시 받는다 — 타입으로 표현되지 않는 제약이다. */
export const buildGiftOrderSearchParams = ({
  donationBusinessId,
  institutionId,
  institutionName,
  initialAmount,
  initialPurpose,
  productNumber,
  optionItemIds = [],
  quantity = 1,
}: {
  donationBusinessId?: number;
  institutionId?: number;
  institutionName?: string;
  initialAmount?: number;
  initialPurpose?: string;
  productNumber?: string;
  optionItemIds?: number[];
  quantity?: number;
}) => ({
  donationBusinessId,
  institutionId,
  institutionName,
  initialAmount,
  initialPurpose,
  productNumber,
  optionItemIds: serializeOptionItemIds(optionItemIds),
  quantity: productNumber ? quantity : undefined,
});

export const buildDonationProductInfo = (
  productNumber: string,
  giftProduct: GiftProductSource,
  optionItemIds?: number[],
): DonationProductInfo => {
  const selectedOptionItems = (giftProduct.optionGroupInfoList ?? []).flatMap(
    (optionGroup) =>
      optionGroup.optionItemInfoList?.filter((optionItem) =>
        optionItemIds?.includes(optionItem.optionItemId),
      ) ?? [],
  );

  const optionExtraProductPriceTotal = selectedOptionItems.reduce(
    (total, optionItem) => total + (optionItem.extraProductPrice ?? 0),
    0,
  );

  const optionName =
    selectedOptionItems
      .map((optionItem) => optionItem.itemName)
      .filter(Boolean)
      .join(' / ') || undefined;

  return {
    productNumber,
    productName: giftProduct.productName,
    imageUrl: giftProduct.mainImageUrl ?? undefined,
    institutionName: giftProduct.institutionName ?? undefined,
    institutionLogoUrl: giftProduct.institutionLogoUrl ?? undefined,
    optionItemIds,
    optionName,
    minDonationAmount: calculateRequiredDonationAmount(
      giftProduct.currentPrice + optionExtraProductPriceTotal,
    ),
    isOfflineProduct: !giftProduct.onlineProductYN,
    deliveryExclusionRegionNames: (
      giftProduct.deliveryExclusionRegionList ?? []
    ).map((region) => region.regionName),
  };
};

export const resolveDeliveryMessage = (deliveryMessage: {
  option: string;
  customText?: string;
}): string | undefined => {
  if (deliveryMessage.option === CUSTOM_DELIVERY_OPTION) {
    return deliveryMessage.customText || undefined;
  }

  return deliveryMessage.option || undefined;
};

//* 상한이 수량 고정값뿐이면 고가 답례품에서 한도의 열 배가 넘는 금액이 CTA에 뜬다. 올해 남은 한도를 아는 화면은 그것을, 모르는 화면은 연간 한도를 기준으로 쓴다.
export const resolveMaxGiftOrderQuantity = (
  unitDonationAmount: number,
  limitAmount: number = ANNUAL_DONATION_LIMIT,
): number => {
  if (unitDonationAmount <= 0) {
    return MAX_GIFT_ORDER_QUANTITY;
  }

  const affordableQuantity = Math.floor(limitAmount / unitDonationAmount);

  return Math.max(1, Math.min(MAX_GIFT_ORDER_QUANTITY, affordableQuantity));
};

export const hasAllRequiredOptions = (
  selectedOptionGroupIds: number[],
  requiredOptionGroupIds: number[],
): boolean => {
  if (requiredOptionGroupIds.length === 0) {
    return true;
  }

  const selectedGroupIds = new Set(selectedOptionGroupIds);

  return requiredOptionGroupIds.every((optionGroupId) =>
    selectedGroupIds.has(optionGroupId),
  );
};

//* 서버는 연간 총한도와 올해 누적 기부액을 따로 준다 — 남은 한도는 두 값의 차다.
export const resolveRemainingDonationLimit = ({
  limitAmount,
  totalDonationAmount,
}: {
  limitAmount?: number;
  totalDonationAmount?: number;
}): number | undefined => {
  if (limitAmount === undefined || totalDonationAmount === undefined) {
    return undefined;
  }

  return Math.max(limitAmount - totalDonationAmount, 0);
};

export const isDonationSubmitDisabled = ({
  notAvailable,
  donationAmount,
  hasPurpose,
  hasInvalidUnit,
  minimumRequiredAmount,
  remainingLimitAmount,
  isAddressMissing,
  isDeliveryExcluded,
}: {
  notAvailable: boolean;
  donationAmount: number;
  hasPurpose: boolean;
  hasInvalidUnit: boolean;
  minimumRequiredAmount: number;
  remainingLimitAmount?: number;
  isAddressMissing: boolean;
  isDeliveryExcluded: boolean;
}): boolean => {
  return (
    notAvailable ||
    !donationAmount ||
    !hasPurpose ||
    hasInvalidUnit ||
    donationAmount < minimumRequiredAmount ||
    (remainingLimitAmount !== undefined &&
      donationAmount > remainingLimitAmount) ||
    isAddressMissing ||
    isDeliveryExcluded
  );
};

export type DonationBlockedTarget =
  | 'availability'
  | 'address'
  | 'amount'
  | 'purpose'
  | 'yearEndGift'
  | 'deliveryConsent'
  | 'agreement';

export type DonationBlockedReason = {
  message: string;
  target: DonationBlockedTarget;
};

export const resolveDonationBlockedReason = ({
  notAvailable,
  isAddressMissing,
  donationAmount,
  hasPurpose,
  hasInvalidUnit,
  minimumRequiredAmount,
  overLimitMessage,
  yearEndGiftBlockedReason,
  isDonationAgreed,
}: {
  notAvailable: boolean;
  isAddressMissing: boolean;
  donationAmount: number;
  hasPurpose: boolean;
  hasInvalidUnit: boolean;
  minimumRequiredAmount: number;
  overLimitMessage: string | null;
  yearEndGiftBlockedReason: string | null;
  isDonationAgreed: boolean;
}): DonationBlockedReason | null => {
  if (notAvailable) {
    return { message: '기부 가능여부 먼저 확인하기', target: 'availability' };
  }

  if (isAddressMissing) {
    return { message: '배송지를 입력해주세요', target: 'address' };
  }

  if (!donationAmount) {
    return { message: '기부 금액을 입력해주세요', target: 'amount' };
  }

  if (!hasPurpose) {
    return { message: '기부 용도를 선택해주세요', target: 'purpose' };
  }

  if (hasInvalidUnit) {
    return { message: '100원 단위로만 기부할 수 있어요', target: 'amount' };
  }

  if (donationAmount < minimumRequiredAmount) {
    return {
      message: `${minimumRequiredAmount.toLocaleString()}원 이상 입력해주세요`,
      target: 'amount',
    };
  }

  if (overLimitMessage) {
    return { message: overLimitMessage, target: 'amount' };
  }

  if (yearEndGiftBlockedReason) {
    return {
      message: yearEndGiftBlockedReason,
      target:
        yearEndGiftBlockedReason === YEAR_END_DELIVERY_CONSENT_REQUIRED
          ? 'deliveryConsent'
          : 'yearEndGift',
    };
  }

  if (!isDonationAgreed) {
    return { message: '약관에 동의해주세요', target: 'agreement' };
  }

  return null;
};

export type DonationSubmitAction = 'disabled' | 'agree' | 'submit';

export const resolveDonationSubmitAction = ({
  isFormSubmitDisabled,
  isDonationAgreed,
}: {
  isFormSubmitDisabled: boolean;
  isDonationAgreed: boolean;
}): DonationSubmitAction => {
  if (isFormSubmitDisabled) {
    return 'disabled';
  }

  if (!isDonationAgreed) {
    return 'agree';
  }

  return 'submit';
};

//* 진입 수량만큼 곱하지 않으면 최소 기부금액에 미달해 제출 버튼이 처음부터 잠긴다.
export const resolveInitialDonationAmount = ({
  initialAmount,
  unitMinimumAmount,
  initialQuantity,
  fallbackAmount,
}: {
  initialAmount?: number;
  unitMinimumAmount?: number;
  initialQuantity: number;
  fallbackAmount: number;
}): number => {
  if (initialAmount !== undefined) {
    return initialAmount;
  }

  if (unitMinimumAmount === undefined) {
    return fallbackAmount;
  }

  return unitMinimumAmount * initialQuantity;
};

//* 「지자체에 맡김」은 donationBusinessId가 없는 항목으로 온다 — 지정기부사업이 하나라도 있으면 그쪽이 기본이다
export const resolveDefaultDonationPurpose = (
  purposes: {
    donationBusinessId?: number | null;
    donationBusinessName: string;
  }[],
  preferredDonationBusinessId?: number,
): string =>
  (
    purposes.find(
      ({ donationBusinessId }) =>
        preferredDonationBusinessId !== undefined &&
        donationBusinessId === preferredDonationBusinessId,
    ) ??
    purposes.find(
      ({ donationBusinessId }) => typeof donationBusinessId === 'number',
    ) ??
    purposes[0]
  )?.donationBusinessName ?? '';
